import React, { useState } from 'react';
import { withRouter } from 'react-router-dom';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  Typography,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { nanoid } from 'nanoid';
import { get } from 'lodash';
import { FeatureFlagTypes } from '../../lib/utils';
import { FeatureFlagCheckbox } from './feature-flag-checkbox';
import { FeatureFlagNumber } from './feature-flag-number';

const styles = {
  modalContainer: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: '30vh',
    maxHeight: '65vh',
    minWidth: '40vh',
    maxWidth: '48vh',
  },
  flagsLabel: {
    flex: 1,
    fontWeight: 500,
  },
  button: {
    width: '140px',
    fontSize: '12px',
    marginBottom: '12px',
  },
};

function BaseFeatureFlagsModal({
  open, handleSave, handleClose, flags, market, showErrorSnackbar,
}) {
  const [updatedFlags, setUpdatedFlags] = useState([]);

  const getFlagForType = (type) => {
    const filteredFlags = flags.filter(
      (flag) => flag.type === type,
    );
    const flag = filteredFlags.length > 0 ? filteredFlags[0]
      : {
        _id: nanoid(24),
        marketId: market._id,
        type,
        isActive: false,
      };
    return flag;
  };

  const emailFlag = getFlagForType(FeatureFlagTypes.OOOZ_NOTIFICATION);
  const oozTaskFlag = getFlagForType(FeatureFlagTypes.OOOZ_TASK);
  const oozCaseFlag = getFlagForType(FeatureFlagTypes.OOOZ_CASE);
  const beaconEmailFlag = getFlagForType(FeatureFlagTypes.BEACON_NOTIFICATION);
  const beaconTaskFlag = getFlagForType(FeatureFlagTypes.BEACON_TASK);
  const beaconCaseFlag = getFlagForType(FeatureFlagTypes.BEACON_CASE);
  const idleTaskFlag = getFlagForType(FeatureFlagTypes.IDLE_VEHICLE_TASK);
  const idleCaseFlag = getFlagForType(FeatureFlagTypes.IDLE_VEHICLE_CASE);
  const idleEmailFlag = getFlagForType(FeatureFlagTypes.IDLE_VEHICLE_NOTIFICATION);
  const lowBatteryFlag = getFlagForType(FeatureFlagTypes.LOW_BATTERY_THRESHOLD);
  const lowBatteryTaskFlag = getFlagForType(FeatureFlagTypes.LOW_BATTERY_TASK);

  const [allFlags] = useState([
    emailFlag,
    oozTaskFlag,
    oozCaseFlag,
    beaconEmailFlag,
    beaconTaskFlag,
    beaconCaseFlag,
    idleTaskFlag,
    idleCaseFlag,
    idleEmailFlag,
    lowBatteryFlag,
    lowBatteryTaskFlag,
  ]);
  const [notifyStatus, setNotifyStatus] = useState(emailFlag.isActive);
  const [oozTaskStatus, setOozTaskStatus] = useState(oozTaskFlag.isActive);
  const [oozCaseStatus, setOozCaseStatus] = useState(oozCaseFlag.isActive);
  const [beaconNotifyStatus, setBeaconNotifyStatus] = useState(beaconEmailFlag.isActive);
  const [beaconTaskStatus, setBeaconTaskStatus] = useState(beaconTaskFlag.isActive);
  const [beaconCaseStatus, setBeaconCaseStatus] = useState(beaconCaseFlag.isActive);
  const [idleTaskStatus, setIdleTaskStatus] = useState(idleTaskFlag.isActive);
  const [idleCaseStatus, setIdleCaseStatus] = useState(idleCaseFlag.isActive);
  const [idleNotifyStatus, setIdleNotifyStatus] = useState(idleEmailFlag.isActive);
  const [lowBatteryStatus, setLowBatteryStatus] = useState(lowBatteryFlag.isActive);
  const [lowBatteryTaskStatus, setLowBatteryTaskStatus] = useState(lowBatteryTaskFlag.isActive);

  const [beaconHours, setBeaconHours] = useState(get(market, 'alerts.userScanDelayHoursThreshold', '') || '');
  const [idleDays, setIdleDays] = useState(get(market, 'alerts.daysVehicleIdleThreshold', '') || '');
  const [batteryThreshold, setBatteryThreshold] = useState(get(market, 'lowBatteryThreshold', '') || '');
  const [vanCapacity, setVanCapacity] = useState(get(market, 'marketVanCapacity', '') || '');
  const [deploymentDuration, setDeploymentDuration] = useState(get(market, 'marketDeploymentDuration', '') || '');
  const [rebalanceDuration, setRebalanceDuration] = useState(get(market, 'marketRebalanceDuration', '') || '');
  const [hoursRunExpires, setHoursRunExpires] = useState(get(market, 'hoursToExpireRun', '') || '');
  const [hoursRunCloses, setHoursRunCloses] = useState(get(market, 'hoursToCloseRun', '') || '');

  const closeModal = () => handleClose();

  const handleStatusChange = (value, flagType) => {
    const status = value;

    const searchIndex = allFlags.findIndex(
      (flag) => flag.type === flagType,
    );

    const newStatus = {
      ...allFlags[searchIndex],
      _id: allFlags[searchIndex]._id,
      isActive: status,
    };

    if (allFlags[searchIndex].isActive !== status) {
      // add the new status to the array if it doesn't match the old status
      if (updatedFlags.length > 0) {
        // check if the array is not empty
        const arrayIndex = updatedFlags.findIndex(
          (flag) => flag.type === flagType,
        );

        if (arrayIndex !== -1) {
          // check if the updated flag already exists in the array
          // and remove it from the array
          updatedFlags.splice(arrayIndex, 1);
        } else {
          // if the updated flag doesn't exist in the array
          // add it to the array
          updatedFlags.push(newStatus);
        }
      } else {
        // add the updated flag to the empty array
        updatedFlags.push(newStatus);
      }
    } else {
      // if the new status matches the old status, remove it from the array
      const notifyIndex = updatedFlags.findIndex(
        (flag) => flag.type === flagType,
      );
      updatedFlags.splice(notifyIndex, 1);
    }

    setUpdatedFlags(updatedFlags);
    if (flagType === FeatureFlagTypes.OOOZ_NOTIFICATION) setNotifyStatus(status);
    if (flagType === FeatureFlagTypes.OOOZ_TASK) setOozTaskStatus(status);
    if (flagType === FeatureFlagTypes.OOOZ_CASE) setOozCaseStatus(status);
    if (flagType === FeatureFlagTypes.BEACON_NOTIFICATION) setBeaconNotifyStatus(status);
    if (flagType === FeatureFlagTypes.BEACON_TASK) setBeaconTaskStatus(status);
    if (flagType === FeatureFlagTypes.BEACON_CASE) setBeaconCaseStatus(status);
    if (flagType === FeatureFlagTypes.IDLE_VEHICLE_TASK) setIdleTaskStatus(status);
    if (flagType === FeatureFlagTypes.IDLE_VEHICLE_CASE) setIdleCaseStatus(status);
    if (flagType === FeatureFlagTypes.IDLE_VEHICLE_NOTIFICATION) setIdleNotifyStatus(status);
    if (flagType === FeatureFlagTypes.LOW_BATTERY_THRESHOLD) setLowBatteryStatus(status);
    if (flagType === FeatureFlagTypes.LOW_BATTERY_TASK) setLowBatteryTaskStatus(status);
  };

  const onSave = () => {
    const beaconHoursBlank = beaconHours === '' || beaconHours < 1;
    const idleDaysBlank = idleDays === '' || idleDays < 1;
    const batteryThresholdBlank = batteryThreshold === '' || batteryThreshold < 1;

    if (beaconHoursBlank && (beaconTaskStatus || beaconCaseStatus)) {
      showErrorSnackbar('Hours not communicating must be greater than 0');
    } else if (idleTaskStatus && idleDaysBlank) {
      showErrorSnackbar('Days idle must be greater than 0');
    } else if (idleCaseStatus && !idleTaskStatus) {
      showErrorSnackbar('Vehicle is idle - pickup tasks required to turn on cases');
    } else if (idleNotifyStatus && !idleCaseStatus) {
      showErrorSnackbar('Vehicle is idle - create a case is required to turn on notification emails');
    } else if (lowBatteryStatus && batteryThresholdBlank && !lowBatteryTaskStatus) {
      showErrorSnackbar('Low battery threshold must be greater than 0');
    } else if (lowBatteryStatus && batteryThresholdBlank && lowBatteryTaskStatus) {
      showErrorSnackbar('Low battery threshold required to turn on task, threshold must be greater than 0');
    } else {
      handleSave(
        updatedFlags,
        parseInt(batteryThreshold, 10),
        parseInt(vanCapacity, 10),
        parseInt(deploymentDuration, 10),
        parseInt(rebalanceDuration, 10),
        parseFloat(hoursRunExpires),
        parseFloat(hoursRunCloses),
        {
          userScanDelayHoursThreshold: parseInt(beaconHours, 10),
          daysVehicleIdleThreshold: parseInt(idleDays, 10),
        },
      );
    }
  };

  const onNumberChange = (e, func, max = Number.MAX_SAFE_INTEGER) => {
    let val = parseInt(e.target.value, 10);

    if (val > max) val = max;
    if (val < 1) val = 1;

    func(val);
  };

  return (
    <Dialog
      PaperProps={{ style: styles.modalContainer }}
      open={open}
      maxWidth="md"
      aria-labelledby="bulk-update-status-dialog-title"
      onClose={closeModal}
      fullWidth
    >
      <DialogTitle id="form-dialog-title">
        <Grid container style={{ padding: 12 }}>
          <Grid item>
            <Typography variant="h6">
              Automation Settings
            </Typography>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent style={{ overflowY: 'scroll' }}>
        <Grid container sx={{ p: 2 }}>
          <Grid item container spacing={1}>
            <Accordion style={{ borderRadius: 0, boxShadow: 'none', margin: 0 }}>
              <AccordionSummary expandIcon={<ExpandMore />} style={{ padding: 0 }}>
                <Grid item xs={12}>
                  <Typography style={styles.flagsLabel}>
                    Vehicle goes out of operational zone
                  </Typography>
                </Grid>
              </AccordionSummary>
              <AccordionDetails style={{ padding: 0 }}>
                <Grid item xs={12}>
                  <FeatureFlagCheckbox
                    label="Create a case"
                    checked={oozCaseStatus}
                    handleStatusChange={handleStatusChange}
                    featureFlagType={FeatureFlagTypes.OOOZ_CASE}
                  />
                  <FeatureFlagCheckbox
                    label="Create a pickup task"
                    checked={oozTaskStatus}
                    handleStatusChange={handleStatusChange}
                    featureFlagType={FeatureFlagTypes.OOOZ_TASK}
                  />
                  <FeatureFlagCheckbox
                    label="Send a notification email"
                    checked={notifyStatus}
                    handleStatusChange={handleStatusChange}
                    featureFlagType={FeatureFlagTypes.OOOZ_NOTIFICATION}
                  />
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Accordion style={{ borderRadius: 0, boxShadow: 'none', margin: 0 }}>
              <AccordionSummary expandIcon={<ExpandMore />} style={{ padding: 0 }}>
                <Grid item xs={12}>
                  <Typography style={styles.flagsLabel}>
                    Vehicle gets a rider scan
                  </Typography>
                </Grid>
              </AccordionSummary>
              <AccordionDetails style={{ padding: 0 }}>
                <Grid item xs={12}>
                  <FeatureFlagNumber
                    label="Hours not communicating"
                    value={beaconHours.toString()}
                    onChange={(e) => onNumberChange(e, setBeaconHours)}
                  />
                  <FeatureFlagCheckbox
                    label="Create a case"
                    checked={beaconCaseStatus}
                    handleStatusChange={handleStatusChange}
                    featureFlagType={FeatureFlagTypes.BEACON_CASE}
                  />
                  <FeatureFlagCheckbox
                    label="Create a pickup task"
                    checked={beaconTaskStatus}
                    handleStatusChange={handleStatusChange}
                    featureFlagType={FeatureFlagTypes.BEACON_TASK}
                  />
                  <FeatureFlagCheckbox
                    label="Send a notification email"
                    checked={beaconNotifyStatus}
                    handleStatusChange={handleStatusChange}
                    featureFlagType={FeatureFlagTypes.BEACON_NOTIFICATION}
                  />
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Accordion style={{ borderRadius: 0, boxShadow: 'none', margin: 0 }}>
              <AccordionSummary expandIcon={<ExpandMore />} style={{ padding: 0 }}>
                <Grid item xs={12}>
                  <Typography style={styles.flagsLabel}>
                    Vehicle is idle
                  </Typography>
                </Grid>
              </AccordionSummary>
              <AccordionDetails style={{ padding: 0 }}>
                <Grid item xs={12}>
                  <FeatureFlagNumber
                    label="Days idle"
                    value={idleDays.toString()}
                    onChange={(e) => onNumberChange(e, setIdleDays)}
                  />
                  <FeatureFlagCheckbox
                    label="Create a pickup task"
                    checked={idleTaskStatus}
                    handleStatusChange={handleStatusChange}
                    featureFlagType={FeatureFlagTypes.IDLE_VEHICLE_TASK}
                  />
                  <FeatureFlagCheckbox
                    label="Create a case"
                    checked={idleCaseStatus}
                    handleStatusChange={handleStatusChange}
                    featureFlagType={FeatureFlagTypes.IDLE_VEHICLE_CASE}
                  />
                  <FeatureFlagCheckbox
                    label="Send a notification email"
                    checked={idleNotifyStatus}
                    handleStatusChange={handleStatusChange}
                    featureFlagType={FeatureFlagTypes.IDLE_VEHICLE_NOTIFICATION}
                  />
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Accordion style={{ borderRadius: 0, boxShadow: 'none', margin: 0 }}>
              <AccordionSummary expandIcon={<ExpandMore />} style={{ padding: 0 }}>
                <Grid item xs={12}>
                  <Typography style={styles.flagsLabel}>
                    Vehicle has low battery
                  </Typography>
                </Grid>
              </AccordionSummary>
              <AccordionDetails style={{ padding: 0 }}>
                <Grid item xs={12}>
                  <FeatureFlagNumber
                    label="Low battery threshold (%)"
                    value={batteryThreshold.toString()}
                    onChange={(e) => onNumberChange(e, setBatteryThreshold, 100)}
                    max={100}
                  />
                  <FeatureFlagCheckbox
                    label="Set automated pickup reason to Low Battery"
                    checked={lowBatteryStatus}
                    handleStatusChange={handleStatusChange}
                    featureFlagType={FeatureFlagTypes.LOW_BATTERY_THRESHOLD}
                  />
                  <FeatureFlagCheckbox
                    label="Create a pickup task"
                    checked={lowBatteryTaskStatus}
                    handleStatusChange={handleStatusChange}
                    featureFlagType={FeatureFlagTypes.LOW_BATTERY_TASK}
                  />
                </Grid>
              </AccordionDetails>
            </Accordion>
            <Accordion style={{ borderRadius: 0, boxShadow: 'none', margin: 0 }}>
              <AccordionSummary expandIcon={<ExpandMore />} style={{ padding: 0 }}>
                <Grid item xs={12}>
                  <Typography style={styles.flagsLabel}>
                    Run Settings
                  </Typography>
                </Grid>
              </AccordionSummary>
              <AccordionDetails style={{ padding: 0 }}>
                <Grid item xs={12}>
                  <FeatureFlagNumber
                    label="Van capacity"
                    value={vanCapacity.toString()}
                    onChange={(e) => onNumberChange(e, setVanCapacity)}
                  />
                  <FeatureFlagNumber
                    label="Deployment duration (hours)"
                    value={deploymentDuration.toString()}
                    onChange={(e) => onNumberChange(e, setDeploymentDuration)}
                  />
                  <FeatureFlagNumber
                    label="Rebalance duration (hours)"
                    value={rebalanceDuration.toString()}
                    onChange={(e) => onNumberChange(e, setRebalanceDuration)}
                  />
                  <FeatureFlagNumber
                    label="Auto expire To Do runs after (hours)"
                    value={hoursRunExpires.toString()}
                    onChange={(e) => onNumberChange(e, setHoursRunExpires)}
                  />
                  <FeatureFlagNumber
                    label="Auto close In Progress runs after (hours)"
                    value={hoursRunCloses.toString()}
                    onChange={(e) => onNumberChange(e, setHoursRunCloses)}
                  />
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid style={{ marginBottom: 16, padding: 16 }}>
          <Button
            onClick={closeModal}
            color="secondary"
            variant="text"
            style={styles.button}
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            color="primary"
            variant="text"
            style={styles.button}
          >
            Save
          </Button>
        </Grid>
      </DialogActions>
    </Dialog>
  );
}

BaseFeatureFlagsModal.defaultProps = {};
BaseFeatureFlagsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleSave: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  showErrorSnackbar: PropTypes.func.isRequired,
  flags: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      isActive: PropTypes.bool,
    }).isRequired,
  ).isRequired,
};

const FeatureFlagsModal = withRouter(BaseFeatureFlagsModal);
export { BaseFeatureFlagsModal, FeatureFlagsModal };
